import { Permissions, RefreshTokenResult } from "@kinde/js-utils";
import { LoginMethodParams, UserProfile } from "@kinde-oss/kinde-auth-react/utils";
import { ChatRole, ICharacter, IUserRoles } from "@next-platform/client-common-core";
import { ConversationSortOrder, Gender, Lorebook } from "@next-platform/packages-ts-types";
import { GroupMembersChatBot } from "@src/components/MembersGroupChat/types";
import { type ReactNode } from "react";

export interface IClaims {
  roles: IUserRoles[];
}

export interface IExternalAuthProviderProps {
  children: ReactNode;
}

export interface IDecodedUser {
  id: string;
  email?: string;
  sub?: string;
  token?: string;
  exp?: number;
  iat?: number;
  external_org_id?: string;
}

export interface IFeatureFlags {
  tts_enabled: boolean;
  gui_v2_enabled: boolean;
}

export interface IAuthUrlParams {
  orgCode?: string;
  connectionId?: string;
  loginHint?: string;
  lang?: string;
  state?: string;
  [key: string]: unknown | undefined;
}

export interface ILoginParams {
  isPartner?: boolean;
  redirectURL?: string;
  token?: string | null;
  app_state?: string | null;
  authUrlParams?: IAuthUrlParams;
}
type RegisterOptions = LoginMethodParams & {
  state?: Record<string, string>;
};

interface IClaimPromiseOption {
  name: string | number;
  value: IUserRoles[];
}

export interface IExternalAuthContext {
  isAuthenticated: boolean;
  isLoading: boolean;
  getToken: () => Promise<string | undefined>;
  logout: () => Promise<void>;
  login: (args: ILoginParams) => Promise<void>;
  register: (options?: RegisterOptions) => Promise<void>;
  permissions: string[] | null;
  user?: IDecodedUser | UserProfile | null;
  isAuthenticationRequired: boolean;
  kindeGetPermissions: () => Promise<Permissions<string>>;
  kindeGetClaim: (key: string) => Promise<IClaimPromiseOption | null>;
  kindeRefreshToken: (arg: { clientId: string; domain: string }) => Promise<RefreshTokenResult>;
  saveToken: (jwt: string) => boolean;
}

export interface IDraftChracter {
  avatar_url?: string;
  avatar_is_nsfw?: boolean;
  creator_user_id?: string;
  creator_username?: string;
  definition_visible?: boolean;
  group_addable?: boolean;
  greeting?: string;
  id?: string;
  character_id?: string;
  is_nsfw?: boolean;
  name?: string;
  num_messages?: number;
  title?: string;
  token_count?: number;
  voice_id?: string;
  visibility?: string;
  tags?: string[];
  lorebooks?: Lorebook[];
  has_lorebooks?: boolean;
  isEdit?: boolean;
  moderationReport?: {
    moderation_status: string;
  };
}

export interface IDraftVoice {
  name?: string;
  desription?: string;
  language?: string;
  voiceGender?: Gender;
  voiceClip?: File[];
}

export interface IDraftGroupCharacter extends IDraftChracter {
  type?: string;
  sub_character_ids?: string[];
  greeting_character_id?: string | null;
  members?: GroupMembersChatBot[];
  sub_characters?: GroupMembersChatBot[];
  scenario?: string;
  persona?: string;
  guidelines?: boolean;
}

export type draftkeyType = string;

export type WaitingQueue = {
  status: string;
  position: number;
  time_left: number;
  bypass: string | boolean;
};

export type GeneratedImage = {
  type: string;
  base64: string;
  negativePrompt: string;
  prompt: string;
  seed: number;
  isNsfw?: boolean;
  /**
   * The backend's handle on the generation behind this image, carried per image because the user
   * generates several and saves one. Echoed back on save; never read here.
   */
  generationRef?: string;
};

export enum ChatError {
  MESSAGE_REGENERATION = "message_regeneration_failed",
  CHARACTER_DETAILS_FETCH = "character_details_fetch_failed",
  MESSAGE_FETCH = "message_fetch_failed",
  MESSAGE_SEND = "message_send_failed",
  MESSAGE_EDIT = "message_edit_failed",
  MESSAGE_CONTINUE = "message_continue_failed",
  MESSAGE_DELETE = "message_delete_failed",
  IMAGE_GENERATION = "image_generation_failed",
  IMAGE_REGENERATION = "image_regeneration_failed",
  CONVERSATION_TITLE_UPDATE = "conversation_title_update_failed",
  MESSAGE_RATING = "message_rating_failed",
}

export interface IChatsConversation {
  character_id: string;
  id: string;
  label: string;
  last_message: {
    content: string;
    createdAt: number;
    role: ChatRole;
  };
  last_text_message: {
    content: string;
    createdAt: number;
    role: ChatRole;
  };
  user_persona_id: string;
  last_message_id: string;
  message_count: number;
  is_last_id?: boolean;
  character?: ICharacter;
}

export enum FormTypes {
  CHARACTER = "character",
  PERSONA = "persona",
}

export interface ILoadConversationsProps {
  limit?: number;
  lastId?: string | null;
  sort?: ConversationSortOrder;
}

export enum AvatarPreset {
  AVATAR_48X48 = "avatar48x48",
  AVATAR_64X64 = "avatar64x64",
  AVATAR_128X128 = "avatar128x128",
  AVATAR_256X256 = "avatar256x256",
  AVATAR_400X400 = "avatar400x400",
  AVATAR_640X640 = "avatar640x640",
  IMAGE_500X650 = "image500x650",
  IMAGE_640X832 = "image640x832",
  IMAGE_960X1248 = "image960x1248",
}

export enum CreationPaths {
  CHATBOTS = "chatbots",
  GROUPS = "groups",
  LOREBOOKS = "lorebooks",
  RULEBOOKS = "rulebooks",
  VOICES = "voices",
}
